import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Armchair, LayoutDashboard, BookOpen, Users, BarChart3, LogOut, Sparkles,
  Library as LibraryIcon, Wallet, ClipboardList, Flame, Crown, Target,
  Award, Gauge, Languages, ClipboardCheck, MessageSquare, Settings,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NextStepIndicator from "@/components/NextStepIndicator";
import PageTransition from "@/components/PageTransition";
import CelebrationFlash from "@/components/CelebrationFlash";
import { useI18n, ENGLISH_ONLY } from "@/lib/i18n";
import { getPageAccent } from "@/lib/pageAccents";

const navItemBase =
  "relative flex items-center gap-3 pl-4 pr-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 group";
// Sidebar lives on the Canales-blue background, so default text is light.
const inactive = "text-white/75 hover:bg-white/10 hover:text-white";
const active = "bg-white font-semibold shadow-sm";

// Accent color per nav group (sidebar section header).
// Picked to feel distinct on the navy background while staying brand-safe.
const GROUP_ACCENTS = {
  admin: "#F4B81F",         // yellow Canales (highest authority)
  learning: "#93C5FD",       // light blue (knowledge)
  operations: "#34D399",     // emerald (process / money flow)
  culture: "#FCD34D",         // soft gold (people values)
  people: "#FCA5A5",          // soft red/coral (recognition)
  default: "#FFFFFF",
};

function NavGroup({ label, children, accent = GROUP_ACCENTS.default }) {
  return (
    <div className="mb-4">
      <p
        className="text-[10px] uppercase tracking-[0.2em] px-3 mb-1.5 font-semibold"
        style={{ color: accent }}
      >
        {label}
      </p>
      <nav className="flex flex-col gap-0.5">{children}</nav>
    </div>
  );
}

function NavItem({ to, end = false, icon: Icon, children, testid, comingSoon = false, comingSoonLabel = "Pronto", accent = "#FFFFFF" }) {
  if (comingSoon) {
    return (
      <div
        className={`${navItemBase} text-white/40 cursor-not-allowed select-none`}
        data-testid={testid}
        title={comingSoonLabel}
      >
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        <span className="flex-1">{children}</span>
        <span className="text-[9px] uppercase tracking-[0.18em] text-white/60 bg-white/10 px-1.5 py-0.5 rounded">
          {comingSoonLabel}
        </span>
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `${navItemBase} ${isActive ? active : inactive}`}
      data-testid={testid}
      style={({ isActive }) => isActive ? { color: accent } : undefined}
    >
      {({ isActive }) => (
        <>
          {/* Vertical pill on the left — shows on hover (faded) and active (solid).
              Driven by the page accent so sidebar visually connects to its page. */}
          <span
            aria-hidden
            className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full transition-opacity duration-150 ${
              isActive ? "opacity-100" : "opacity-0 group-hover:opacity-70"
            }`}
            style={{ backgroundColor: accent }}
          />
          <Icon
            className="h-4 w-4 transition-colors"
            strokeWidth={1.5}
            style={isActive ? { color: accent } : undefined}
          />
          {children}
        </>
      )}
    </NavLink>
  );
}

function LanguageToggle() {
  const { locale, setLocale, t } = useI18n();
  // Hidden in English-only mode (controlled by ENGLISH_ONLY in lib/i18n.jsx).
  if (ENGLISH_ONLY) return null;
  return (
    <div
      className="inline-flex rounded-full bg-[#F3F4F6] p-0.5 border border-[#E5E7EB]"
      role="group"
      aria-label={t("topbar.language_toggle_aria")}
      data-testid="language-toggle"
    >
      {["es", "en"].map((code) => {
        const isActive = locale === code;
        return (
          <button
            key={code}
            onClick={() => setLocale(code)}
            className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] rounded-full transition-colors ${
              isActive ? "bg-white text-[#1B2E7A] shadow-sm" : "text-[#6B7280] hover:text-[#1A1A1A]"
            }`}
            data-testid={`locale-${code}`}
            aria-pressed={isActive}
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();

  if (!user) return null;
  const isAdmin = user.role === "admin";
  const isManager = !isAdmin && (user.user_type === "manager");

  return (
    <div className="min-h-screen flex bg-[#FFFFFF]" data-testid="app-layout">
      {/* Sidebar — Canales blue background */}
      <aside className="hidden md:flex flex-col w-64 bg-[#1B2E7A] border-r border-[#13205A] sticky top-0 h-screen p-5 overflow-y-auto text-white">
        <button
          onClick={() => navigate(isAdmin ? "/admin" : "/dashboard")}
          className="flex items-center gap-3 mb-8 group"
          data-testid="brand-link"
        >
          <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center group-hover:ring-2 group-hover:ring-[#F4B81F] transition-all overflow-hidden p-0.5">
            <img src="/icon-192.png" alt="Canales" className="h-full w-full object-contain" />
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">{t("brand.subtitle")}</p>
            <p className="font-heading text-base text-white -mt-0.5">{t("brand.title")}</p>
          </div>
        </button>

        {isAdmin && (
          <NavGroup label={t("sidebar.group.admin")} accent={GROUP_ACCENTS.admin}>
            <NavItem to="/admin" end icon={BarChart3} testid="nav-admin" accent={getPageAccent("admin")}>
              {t("nav.admin")}
            </NavItem>
            <NavItem to="/admin/assignments" icon={Sparkles} testid="nav-assignments" accent={getPageAccent("admin")}>
              {t("nav.assignments")}
            </NavItem>
            <NavItem to="/admin/scenarios" icon={Sparkles} testid="nav-admin-scenarios" accent="#F4B81F">
              Scenarios
            </NavItem>
          </NavGroup>
        )}

        <NavGroup label={t("sidebar.group.learning")} accent={GROUP_ACCENTS.learning}>
          <NavItem
            to={isAdmin ? "/dashboard" : "/dashboard"}
            end
            icon={isAdmin ? BookOpen : LayoutDashboard}
            testid={isAdmin ? "nav-courses-admin" : "nav-dashboard"}
            accent={getPageAccent("dashboard")}
          >
            {isAdmin ? t("nav.courses_admin") : t("nav.dashboard")}
          </NavItem>
          <NavItem
            to="/biblioteca"
            icon={LibraryIcon}
            testid={isAdmin ? "nav-library-admin" : "nav-library"}
            accent={getPageAccent("library")}
          >
            {t("nav.library")}
          </NavItem>
          <NavItem
            to={isAdmin ? "/admin/procesos" : "/procesos"}
            icon={ClipboardList}
            testid="nav-procesos"
            accent={getPageAccent("procesos")}
          >
            {t("nav.processes")}
          </NavItem>
          <NavItem
            to="/comunidad"
            icon={Users}
            testid="nav-comunidad"
            accent={getPageAccent("team")}
          >
            {t("nav.community")}
          </NavItem>
          {!isAdmin && (
            <NavItem to="/idiomas" icon={Languages} testid="nav-idiomas" accent={getPageAccent("languages")}>
              {t("nav.languages")}
            </NavItem>
          )}
          {isAdmin && (
            <NavItem to="/admin/idiomas" icon={Languages} testid="nav-admin-idiomas" accent={getPageAccent("languages")}>
              {t("nav.languages_admin")}
            </NavItem>
          )}
        </NavGroup>

        <NavGroup label={t("sidebar.group.operations")} accent={GROUP_ACCENTS.operations}>
          <NavItem
            to={isAdmin ? "/admin/financiamiento" : "/financiamiento"}
            icon={Wallet}
            testid={isAdmin ? "nav-financing-admin" : "nav-financing"}
            accent={getPageAccent("financiamiento")}
          >
            {t("nav.financing")}
          </NavItem>
        </NavGroup>

        <NavGroup label={t("sidebar.group.culture")} accent={GROUP_ACCENTS.culture}>
          <NavItem to="/seccion/motivacion" icon={Flame} testid="nav-motivacion" accent={getPageAccent("motivacion")}>
            {t("nav.motivation")}
          </NavItem>
          <NavItem to="/seccion/liderazgo" icon={Crown} testid="nav-liderazgo" accent={getPageAccent("liderazgo")}>
            {t("nav.leadership")}
          </NavItem>
          <NavItem to="/seccion/estrategia" icon={Target} testid="nav-estrategia" accent={getPageAccent("estrategia")}>
            {t("nav.strategy")}
          </NavItem>
        </NavGroup>

        <NavGroup label={t("sidebar.group.people")} accent={GROUP_ACCENTS.people}>
          <NavItem
            to={isAdmin ? "/admin/recompensas" : "/recompensas"}
            icon={Award}
            testid="nav-reconocimiento"
            accent={getPageAccent("rewards")}
          >
            {t("nav.rewards")}
          </NavItem>
          <NavItem
            to={isAdmin ? "/admin/evaluaciones" : "/evaluaciones"}
            icon={ClipboardCheck}
            testid="nav-evaluaciones"
            accent={getPageAccent("evaluations")}
          >
            {t("nav.evaluations")}
          </NavItem>
          {(isManager || isAdmin) && (
            <NavItem to="/mi-equipo" icon={Users} testid="nav-my-team" accent={getPageAccent("team")}>
              {t("nav.my_team")}
            </NavItem>
          )}
          {isAdmin && (
            <NavItem to="/admin/eval-managers" icon={Gauge} testid="nav-mgr-eval" accent={getPageAccent("team")}>
              {t("nav.mgr_eval")}
            </NavItem>
          )}
          {isAdmin && (
            <NavItem to="/admin/sms" icon={MessageSquare} testid="nav-admin-sms" accent={getPageAccent("admin")}>
              {t("nav.sms")}
            </NavItem>
          )}
        </NavGroup>

        <div className="mt-auto pt-6 border-t border-white/15">
          {isAdmin ? (
            <button
              type="button"
              onClick={() => navigate("/admin/settings")}
              className="w-full flex items-center gap-3 px-2 mb-3 group rounded-md py-1.5 hover:bg-white/10 transition-colors"
              data-testid="user-profile-link"
              title={t("settings.title")}
            >
              <div className="h-9 w-9 rounded-full bg-[#F4B81F] text-[#1B2E7A] flex items-center justify-center font-bold text-sm shrink-0">
                {user.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0 text-left flex-1">
                <p className="text-sm font-medium text-white truncate group-hover:text-[#F4B81F]" data-testid="user-name">
                  {user.name}
                </p>
                <p className="text-xs text-white/60 truncate inline-flex items-center gap-1">
                  {t("user.role.admin")}
                  <Settings className="h-3 w-3 text-white/60 group-hover:text-[#F4B81F]" strokeWidth={1.6} />
                </p>
              </div>
            </button>
          ) : (
            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="h-9 w-9 rounded-full bg-[#F4B81F] text-[#1B2E7A] flex items-center justify-center font-bold text-sm shrink-0">
                {user.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate" data-testid="user-name">
                  {user.name}
                </p>
                <p className="text-xs text-white/60 truncate">
                  {t("user.role.salesperson")}
                </p>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 text-sm text-white/75 hover:text-white px-3 py-2 rounded-md hover:bg-white/10 transition-colors"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            {t("user.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between px-5 md:px-10 py-4">
            <div className="md:hidden flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-white border border-[#E5E7EB] overflow-hidden p-0.5">
                <img src="/icon-192.png" alt="Canales" className="h-full w-full object-contain" />
              </div>
              <p className="font-heading text-base text-[#1A1A1A]">Canales</p>
            </div>
            <div className="hidden md:block">
              <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">
                {isAdmin ? t("topbar.admin_view") : t("topbar.platform")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              <NextStepIndicator />
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => navigate("/admin/settings")}
                  className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#1B2E7A]/10 text-[#1B2E7A] text-xs font-medium hover:bg-[#1B2E7A] hover:text-white transition-colors"
                  data-testid="topbar-admin-link"
                  title={t("settings.title")}
                >
                  <Users className="h-3 w-3" strokeWidth={2} /> {user.name}
                </button>
              )}
              <button
                onClick={logout}
                className="md:hidden text-[#374151]"
                data-testid="logout-button-mobile"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </header>

        <div className="px-5 md:px-10 py-8 md:py-12 max-w-7xl mx-auto">
          <PageTransition>{children}</PageTransition>
        </div>
      </main>
      <CelebrationFlash />
    </div>
  );
}
